/**
 * Generic interface for structured error messages with a title and message.
 * Used throughout the application for displaying user-friendly error messages
 * in components like AlertBanner, Toast notifications, status messages, etc.
 */
export interface StructuredErrorMessage {
  title: string
  message: string
}

/**
 * Custom Error class that extends Error but preserves structured error information.
 * This allows it to be thrown and caught like a normal Error while maintaining
 * the title/message structure for UI display.
 */
export class StructuredError extends Error implements StructuredErrorMessage {
  public readonly title: string

  constructor(title: string, message: string) {
    // Set the Error.message to the message part for compatibility
    super(message)
    this.title = title

    // Make name non-enumerable so it doesn't get serialized
    Object.defineProperty(this, 'name', {
      value: 'StructuredError',
      enumerable: false,
      writable: false,
      configurable: false,
    })

    // Make message enumerable so it gets copied when the object is serialized
    Object.defineProperty(this, 'message', {
      value: message,
      enumerable: true,
      writable: false,
      configurable: false,
    })

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, StructuredError)
    }
  }

  /**
   * Returns the message part for string conversion and tracking
   */
  toString(): string {
    return this.message
  }

  /**
   * Returns the full structured error for UI display
   */
  toStructured(): StructuredErrorMessage {
    return {
      title: this.title,
      message: this.message,
    }
  }

  /**
   * Custom JSON serialization to ensure title and message are included
   * when JSON.stringify() is called on this error
   */
  toJSON(): StructuredErrorMessage {
    return {
      title: this.title,
      message: this.message,
    }
  }
}
